import type { ShopFacts } from '@croissants/contracts';
import { type OfferedDay, todayOffer } from './stay';

/**
 * What mode B2's delivery popup states, derived from answers the platform
 * already gave (founder decision, 2026-09-21; PRD GST-045).
 *
 * Two figures, and neither is this app's own: WHEN the bound shop next opens
 * comes off `GET /g/shop-facts`, and HOW SOON an order for now can arrive comes
 * off the picker's own Soonest offer. Both take the platform's clock as an
 * argument - nothing here reads the device's (TST-003).
 */

/** When the bound shop next opens its door. */
export interface NextOpening {
  /** Against the shop's own today, as the platform's clock decided it. */
  readonly kind: ShopFacts['nextAvailableDayKind'];
  readonly serviceDay: string;
  /** The opening time as the baker typed it, `07:00`. */
  readonly time: string;
}

/** `HH:MM` on a 24-hour clock in the given zone - comparable as a string. */
function localClock(nowMs: number, timeZone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone,
  }).format(new Date(nowMs));
}

/**
 * The next time the shop opens, or null when the answer states none.
 *
 * `deliverySpans` are the shop's OPENING HOURS on `nextAvailableServiceDay`.
 * On a later morning the first span is the answer. On TODAY the shop may be
 * between two services - shut at 14:00, open again at 16:30 - so the answer is
 * the first span that has not started yet, and a today with none left states
 * nothing rather than an hour that is already behind the guest.
 */
export function nextOpening(
  facts: Pick<
    ShopFacts,
    'nextAvailableServiceDay' | 'nextAvailableDayKind' | 'deliverySpans' | 'pickupTimeZone'
  >,
  nowMs: number,
): NextOpening | null {
  const serviceDay = facts.nextAvailableServiceDay;
  if (serviceDay === null) return null;
  const kind = facts.nextAvailableDayKind;
  const clock = kind === 'TODAY' ? localClock(nowMs, facts.pickupTimeZone) : null;
  const span = facts.deliverySpans.find((entry) => clock === null || entry.start > clock);
  return span === undefined ? null : { kind, serviceDay, time: span.start };
}

/**
 * "Delivery in about X min": the minutes from now to the start of
 * today's Soonest offer, rounded UP to five.
 *
 * That start is the server's own figure - now, plus the answer allowance, plus
 * the counter's preparation time, plus the travel from THIS shop (mode B2 is
 * bound to one, so it is not a union across houses) - and it is stated as the
 * earliest, never as a range: the range is still named once, on the review
 * screen, from the quote (RTE-034). It is an ESTIMATE made before any counter
 * has answered: once one does, the bag is due at its yes plus that figure
 * (founder decision 2026-10-07), and the courier's answer is what the guest
 * reads next. Null when no order for now is on offer.
 */
export function earliestArrivalMinutes(
  days: readonly OfferedDay[] | undefined,
  nowMs: number,
): number | null {
  const today = todayOffer(days ?? []);
  const soonest = today?.windows.find((entry) => entry.soonest === true && entry.selectable);
  if (soonest === undefined) return null;
  const minutes = (new Date(soonest.window.start).getTime() - nowMs) / 60_000;
  // To the minute, never to five (founder, 2026-09-21).
  return Math.max(1, Math.ceil(minutes));
}

/**
 * THE FIGURE THE GUEST READS: the platform's own `orderNowMinutes` - one minute
 * to accept, the shop's measured preparation time, the provider's travel for
 * this pair (`GET /g/shop-facts`). It arrives with the facts that open the
 * popup, so it is there when the popup is.
 *
 * `fallback` is the picker's derivation above, for the one case the field is
 * absent: a worker older than it, during a deploy. `== null` covers both null
 * (no order for now) and that absence; only the absence falls back.
 */
export function orderNowMinutesOf(
  facts: { readonly orderNowMinutes?: number | null } | undefined,
  fallback: () => number | null,
): number | null {
  if (facts === undefined) return null;
  return facts.orderNowMinutes === undefined ? fallback() : facts.orderNowMinutes;
}
